import { shaOfUri } from '../storage/blobStore.ts';
import { storage } from '../storage/index.ts';
import { fileFromState, savedPartsChanged } from '../storage/record.ts';
import { syncHooks } from '../sync/hooks.ts';
import { pendingDeletes, projectState, useDocument } from './documentStore.ts';

/**
 * Saves the open project 500 ms after any change to its saved parts (docs/spec/01 §7),
 * and immediately on flush (leaving the editor, page hide).
 */
const DELAY = 500;
let timer: ReturnType<typeof setTimeout> | undefined;
let installed = false;
let saving: Promise<void> = Promise.resolve();

export function installAutosave(): void {
  if (installed) return;
  installed = true;
  useDocument.subscribe((s, prev) => {
    if (!s.file || !s.projectId) return;
    if (prev.projectId !== s.projectId) return; // a project was just opened
    if (!savedPartsChanged(s, prev)) return;
    clearTimeout(timer);
    timer = setTimeout(() => void flushAutosave(), DELAY);
  });
  addEventListener('pagehide', () => void flushAutosave());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushAutosave();
  });
}

/** Saves now if a save is pending. Resolves when the write is done. */
export function flushAutosave(): Promise<void> {
  if (timer === undefined) return saving;
  clearTimeout(timer);
  timer = undefined;
  const { file } = useDocument.getState();
  if (!file) return saving;
  const next = fileFromState(projectState(), file, Date.now());
  useDocument.getState().setFile(next);
  const deletes = pendingDeletes
    .splice(0)
    .map(shaOfUri)
    .filter((s): s is string => !!s);
  saving = saving.then(async () => {
    await storage().projects.save(next);
    syncHooks.projectSaved(next.id);
    await storage().projects.collect(deletes);
  });
  return saving;
}

/** True when a save is scheduled but not written yet. */
export function hasPendingSave(): boolean {
  return timer !== undefined;
}
